import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface EditPostParams {
  postId: string;
  postType: 'bulletin' | 'loop';
  updates: {
    content?: string;
    removedCommunityIds?: string[];
    removeImage?: boolean;
    is_artstream?: boolean;
  };
}

export function useEditPost() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ postId, postType, updates }: EditPostParams) => {
      const now = new Date().toISOString();

      // 1. Update the post content and set edited_at timestamp
      if (updates.content !== undefined || updates.removeImage || updates.is_artstream !== undefined) {
        const tableName = postType === 'bulletin' ? 'bulletins' : 'loops';
        const updateData: any = {
          edited_at: now,
        };

        if (updates.content !== undefined) {
          updateData.content = updates.content;
        }

        if (updates.removeImage) {
          updateData.image_url = null;
          updateData.thumbnail_url = null;
        }

        if (updates.is_artstream !== undefined) {
          updateData.is_artstream = updates.is_artstream;
        }

        const { error } = await supabase
          .from(tableName)
          .update(updateData)
          .eq('id', postId);

        if (error) throw error;
      }

      // 2. Remove communities if specified
      if (updates.removedCommunityIds && updates.removedCommunityIds.length > 0) {
        const tableName = postType === 'bulletin' ? 'bulletin_communities' : 'loop_communities';
        
        const { error } = await supabase
          .from(tableName)
          .delete()
          .eq(postType === 'bulletin' ? 'bulletin_id' : 'loop_id', postId)
          .in('community_id', updates.removedCommunityIds);

        if (error) throw error;
      }

      return { postId, postType };
    },
    onSuccess: (data) => {
      // Invalidate relevant queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ['bulletins'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['loops'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['user-bulletins'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['user-loops-and-reblogs'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['infinite-bulletins'], exact: false }); // Artstream feed
      queryClient.invalidateQueries({ queryKey: ['loop-communities'], exact: false }); // Community hashtags
      
      if (data.postType === 'bulletin') {
        queryClient.invalidateQueries({ queryKey: ['bulletin', data.postId] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['loop', data.postId] });
      }

      toast({
        title: 'Post updated',
        description: 'Your changes have been saved successfully.',
      });
    },
    onError: (error) => {
      console.error('Error updating post:', error);
      toast({
        title: 'Update failed',
        description: 'There was an error updating your post. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

// Helper hook to check if user can edit a post
export function useCanEditPost(post?: { user_id?: string; profiles?: { id: string } }) {
  const { user } = useAuth();
  
  if (!post || !user) return false;
  
  // Check if current user is the post owner
  const postUserId = post.user_id || post.profiles?.id;
  return postUserId === user.id;
}
